import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid2';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Copyright from '../internals/components/Copyright';
import ChartBatteryPacksByType from '../components/ChartBatteryPacksByType';
import CustomizedTreeView from '../components/CustomizedTreeView';
import BatteryDataGrid from '../components/BatteryDataGrid';
import HighlightedCard from '../components/HighlightedCard';
import PageViewsBarChart from '../components/PageViewsBarChart';
import SessionsChart from '../components/SessionsChart';
import StatCard from '../components/StatCard';
import FleetMap from '../components/FleetMap';
import Card from '@mui/material/Card';
import { useTheme } from '@mui/material/styles';
import { useParams } from 'react-router-dom';
import HistoryIcon from '@mui/icons-material/History';
import { capitalize, Chip } from '@mui/material';
import { formatTimeAgo } from '../internals/utils';
import FleetDataGrid from '../components/FleetDataGrid';

const models = { 'IGT Red': { 'src': '/assets/img/IGT Red.png', 'color': '#ff3780' }, 'IGT Blu': { 'src': '/assets/img/IGT Blu.png', 'color': '#46aef7' } };

// Demo data for the FleetDetails component
const demoFleetDetails = {
  tag: 'MSR',
  model: 'Warivo L-2',
  motor: 'Hub- BLDC 10 inch',
  controller: '48V/60V',
  size: '250',
  added: '1m ago',
};


export function FleetDetails({ fleetDetails: { fleet_name, fleet_owner, fleet_vehicle, vehicle_controller, vehicle_motor, createdAt } }) {
  const theme = useTheme();
  const { id } = useParams(); // This will get the `id` from the URL
  return (
    <Grid
      container
      spacing={2}
      columns={12}
    >
      <Card variant="outlined" sx={{ display: 'flex', alignItems: 'center', width: '100%', height: '180px' }}>
        <Box sx={{ width: '35%', height: '100%' }}>
          <img src='/assets/img/Warivo L-2.png' width="100%" height="100%" style={{ objectFit: 'contain' }} />
          {/* <img src={models[model] != null ? models[model].src : '/assets/img/IGT Red.png'} width="100%" height="100%" style={{ objectFit: 'contain' }} /> */}
        </Box>
        <Box>
          <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
            {fleet_name}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: 16, color: theme.palette.text.secondary }}>Vehicle: <span style={{ color: theme.palette.info.main }}>{fleet_vehicle}</span></Typography>
          <Typography variant="body2" sx={{ fontSize: 16, color: theme.palette.text.secondary }}>
            Motor: <span style={{ color: theme.palette.info.main }}>{vehicle_motor}</span></Typography>
          <Typography variant="body2" sx={{ fontSize: 16, color: theme.palette.text.secondary }}>
            Controller: <span style={{ color: theme.palette.success.main }}>{vehicle_controller}</span></Typography>
          <Typography variant="body2" sx={{ fontSize: 16, color: theme.palette.text.secondary }}>
            Owner: <span style={{ color: theme.palette.info.main }}>{fleet_owner}</span></Typography>
          <Typography variant="body2" sx={{ fontSize: 16, color: theme.palette.text.secondary }}>
            Added: <span style={{ color: theme.palette.info.main }}>{(new Date(createdAt)).toLocaleDateString()}</span></Typography>
        </Box>
      </Card>
    </Grid >
  );
}

export default function Fleet() {
  const initial_data = [
    {
      title: 'SoC',
      value: '--',
      interval: 'Last 30 days',
      trend: 'up',
      data: [],
    },
    {
      title: 'Voltage',
      value: '--',
      interval: 'Last 30 days',
      trend: 'down',
      data: [],
    },
    {
      title: 'Current',
      value: '--',
      interval: 'Last 30 days',
      trend: 'neutral',
      data: [],
    },
    {
      title: 'Avg. Battery Cell Voltage',
      value: '--',
      interval: 'Last 30 days',
      trend: 'up',
      data: [],
    },
    {
      title: 'Avg. Battery Cell Temperature',
      value: '--',
      interval: 'Last 30 days',
      trend: 'neutral',
      data: [],
    },
    {
      title: 'Remaining Charge capacity',
      value: '--',
      interval: 'Last 30 days',
      trend: 'neutral',
      data: [],
    },
    {
      title: 'Cycle Number',
      value: '--',
      interval: 'Last 30 days',
      trend: 'up',
      data: [],
    },
  ];
  const initial_details =
  {
    tag: '--',
    model: '--',
    motor: '--',
    controller: '--',
    size: '--',
    added: '--',
  };
  const initial_location =
  {
    lat: 28.370108,
    lng: 76.922603
  };
  const units = ['%', 'V', 'A', 'V', '°C', 'Ah', ''];
  const { id } = useParams(); // This will get the `id` from the URL
  const [data, setData] = useState(initial_data);
  const [vehicles, setVehicles] = useState([]);
  const [details, setDetails] = useState(initial_details);
  const [location, setLocation] = useState(initial_location);
  const [timestamps, setTimestamps] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch('https://0h5n8j55d5.execute-api.ap-south-1.amazonaws.com/dev/fleets/'+id);
        const fleetDataResponse = await response.json();
        const updatedDetails = JSON.parse(JSON.stringify(initial_details));
        if (fleetDataResponse.meta !== undefined) {
          for (const key in fleetDataResponse.meta) {
            switch (key) {
              default:
                updatedDetails[key] = fleetDataResponse.meta[key];
                break;
            }
          }
        }
        setDetails(updatedDetails);
        const fleetData = [];
        fleetDataResponse.vehicles.forEach((vehicle, index) => {
          const vehicleData = { id: index };
          Object.keys(vehicle).forEach(key => {
            switch (key) {
              default:
                vehicleData[key] = vehicle[key];
            }
          });
          fleetData.push(vehicleData);
        });
        setVehicles(fleetData);
        // console.log(vehicles);
      } catch (error) {
        setError(error);
      }
    };

    fetchData();
  }, []);

  return (
    <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
      {/* cards */}
      <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
        Overview&emsp;<Chip icon={<HistoryIcon />} label={formatTimeAgo(timestamps.at(-1))} />
      </Typography>
      <Grid
        container
        spacing={2}
        columns={12}
        sx={{ mb: (theme) => theme.spacing(2) }}
      >
        <Grid container
          spacing={2}
          columns={12} size={{ xs: 12, lg: 6 }} sx={{ height: { xs: 'calc(60vh + 180px + 16px)', lg: '80vh' } }}>
          <Grid  size={{ xs: 12 }}>
            <FleetDetails fleetDetails={details} />
          </Grid>
          <Grid  size={{ xs: 12 }} sx={{ height: { xs: '60vh', lg: 'calc(80vh - 180px - 16px)' } }}>
            <FleetDataGrid fleetData={vehicles} />
          </Grid>
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }} sx={{ overflow: 'hidden', borderRadius: '8px', border: '1px solid var(--template-palette-divider)', height: { xs: '400px', lg: 'unset' } }}>
          <FleetMap positions={vehicles} imageSrc={models[details.model] != null ? models[details.model].src : '/assets/img/IGT Red.png'} pinColor={models[details.model] != null ? models[details.model].color : '#ff3780'} />
        </Grid>
      </Grid>
      <Copyright sx={{ my: 4 }} />
    </Box >
  );
}
